(() => {
  if (window.__vimaCartDrawerReady) return;
  window.__vimaCartDrawerReady = true;

  const sections = ['cart-drawer', 'cart-icon-bubble'];
  let variantSwapInFlight = false;

  const refreshDrawer = async (open = true) => {
    const [cartResponse, sectionsResponse] = await Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }),
      fetch('/?sections=' + sections.join(','), { headers: { Accept: 'application/json' }, cache: 'no-store' })
    ]);
    if (!cartResponse.ok || !sectionsResponse.ok) throw new Error('refresh');
    const [state, renderedSections] = await Promise.all([cartResponse.json(), sectionsResponse.json()]);
    state.sections = renderedSections;
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.renderContents(state);
      if (!open) drawer.close();
    }
    return state;
  };

  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EUR'
  }).format((Number(cents) || 0) / 100);

  const vimaSizeNumbers = (value) => {
    const matches = String(value || '').match(/\b(1[4-9]|2\d|3\d)(?:\s*[-–]\s*(1[4-9]|2\d|3\d))?/);
    if (!matches) return [];
    return [Number(matches[1]), matches[2] ? Number(matches[2]) : Number(matches[1])];
  };

  const vimaCmRange = (value) => {
    const text = String(value || '').replace(/,/g, '.');
    const range = text.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*cm/i);
    if (range) return { min: Number(range[1]), max: Number(range[2]) };

    const maximum = text.match(/[<≤]\s*(\d+(?:\.\d+)?)\s*cm/i);
    if (maximum) return { min: 0, max: Number(maximum[1]) };

    const single = text.match(/(\d+(?:\.\d+)?)\s*cm/i);
    if (single) {
      const cm = Number(single[1]);
      return { min: cm, max: cm };
    }
    return null;
  };

  const vimaPreferredFit = (cart) => {
    let fallbackSize = '';

    for (const item of cart?.items || []) {
      if (String(item?.properties?._vima_gift || '') === 'true') continue;

      for (const option of item.options_with_values || []) {
        const name = String(option?.name || '').toLowerCase();
        const value = String(option?.value || '');
        const cm = vimaCmRange(value);
        if (cm) return { cm, size: value };

        if (!fallbackSize && (name.includes('pointure') || name.includes('taille') || name.includes('size'))) {
          if (vimaSizeNumbers(value).length) fallbackSize = value;
        }
      }

      for (const value of item.variant_options || []) {
        const cm = vimaCmRange(value);
        if (cm) return { cm, size: String(value) };
        if (!fallbackSize && vimaSizeNumbers(value).length) fallbackSize = String(value);
      }

      const titleCm = vimaCmRange(item.variant_title);
      if (titleCm) return { cm: titleCm, size: String(item.variant_title || '') };
      if (!fallbackSize && vimaSizeNumbers(item.variant_title).length) fallbackSize = String(item.variant_title);
    }

    return { cm: null, size: fallbackSize };
  };

  const vimaVariantForFit = (variants, fit) => {
    const available = (variants || []).filter((variant) => variant.available);
    if (!available.length) return variants?.[0];

    if (fit?.cm) {
      const targetMin = Number(fit.cm.min || fit.cm.max);
      const targetMax = Number(fit.cm.max);

      const candidates = available
        .map((variant) => {
          const option = (variant.options || []).find((value) => vimaCmRange(value));
          const cm = vimaCmRange(option || variant.title);
          return cm ? { variant, cm } : null;
        })
        .filter(Boolean)
        .filter((entry) => entry.cm.max >= targetMax && entry.cm.min <= targetMin)
        .sort((a, b) => a.cm.max - b.cm.max || b.cm.min - a.cm.min);

      if (candidates.length) return candidates[0].variant;

      const safeCandidates = available
        .map((variant) => {
          const option = (variant.options || []).find((value) => vimaCmRange(value));
          const cm = vimaCmRange(option || variant.title);
          return cm ? { variant, cm } : null;
        })
        .filter(Boolean)
        .filter((entry) => entry.cm.max >= targetMax)
        .sort((a, b) => a.cm.max - b.cm.max);

      if (safeCandidates.length) return safeCandidates[0].variant;
    }

    const target = vimaSizeNumbers(fit?.size);
    if (target.length) {
      const exact = available.find((variant) =>
        (variant.options || []).some((option) => {
          const nums = vimaSizeNumbers(option);
          return nums.length && nums[0] === target[0] && nums[1] === target[1];
        })
      );
      if (exact) return exact;
    }

    return available[0];
  };

  const vimaRecentSeed = (existing, fallback) => {
    try {
      const recent = JSON.parse(localStorage.getItem('vimaRecentlyViewedProducts') || '[]');
      const candidate = recent.find((id) => id && !existing.has(String(id)));
      return candidate || fallback;
    } catch {
      return fallback;
    }
  };

  const vimaIsSeasonalRecommendation = (product) => {
    const haystack = [product?.title, product?.handle, product?.product_type].filter(Boolean).join(' ').toLowerCase();
    return !/(piscine|chaussures? d['’]?eau|sandales?|nemo|tijuca)/i.test(haystack);
  };

  const syncCustomSizePicker = (select) => {
    if (!select) return;
    const label = select.closest('.vima-cart__variant-control');
    if (!label) return;

    select.classList.add('vima-cart__variant-select--native-size');

    let picker = label.querySelector('.vima-cart__size-picker');
    if (!picker) {
      picker = document.createElement('div');
      picker.className = 'vima-cart__size-picker';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'vima-cart__size-trigger';
      trigger.setAttribute('aria-expanded', 'false');

      const menu = document.createElement('div');
      menu.className = 'vima-cart__size-menu';
      menu.hidden = true;

      picker.append(trigger, menu);
      label.appendChild(picker);
    }

    const trigger = picker.querySelector('.vima-cart__size-trigger');
    const menu = picker.querySelector('.vima-cart__size-menu');
    const selectedOption = select.options[select.selectedIndex];
    trigger.textContent = selectedOption?.textContent || '';
    menu.replaceChildren();

    Array.from(select.options).forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'vima-cart__size-option';
      item.textContent = option.textContent;
      item.dataset.value = option.value;
      item.disabled = option.disabled;
      item.classList.toggle('is-unavailable', option.disabled);
      item.classList.toggle('is-selected', option.selected);
      menu.appendChild(item);
    });
  };

  const syncCustomColorPicker = (select) => {
    if (!select) return;
    const label = select.closest('.vima-cart__variant-control');
    if (!label) return;

    select.classList.add('vima-cart__variant-select--native-color');

    let picker = label.querySelector('.vima-cart__color-picker');
    if (!picker) {
      picker = document.createElement('div');
      picker.className = 'vima-cart__color-picker';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'vima-cart__color-trigger';
      trigger.setAttribute('aria-expanded', 'false');

      const menu = document.createElement('div');
      menu.className = 'vima-cart__color-menu';
      menu.hidden = true;

      picker.append(trigger, menu);
      label.appendChild(picker);
    }

    const trigger = picker.querySelector('.vima-cart__color-trigger');
    const menu = picker.querySelector('.vima-cart__color-menu');
    const selectedOption = select.options[select.selectedIndex];
    trigger.textContent = selectedOption?.textContent || '';
    menu.replaceChildren();

    Array.from(select.options).forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'vima-cart__color-option';
      item.textContent = option.textContent;
      item.dataset.value = option.value;
      item.disabled = option.disabled;
      item.classList.toggle('is-unavailable', option.disabled);
      item.classList.toggle('is-selected', option.selected);
      menu.appendChild(item);
    });
  };

  const refreshVariantAvailability = (root) => {
    if (!root) return;
    const variants = JSON.parse(root.dataset.variants || '[]');
    const currentOptions = JSON.parse(root.dataset.currentOptions || '[]');
    const controls = Array.from(root.querySelectorAll('[data-vima-variant-option]'));

    const selected = currentOptions.slice();
    controls.forEach((select) => {
      const index = Number(select.dataset.optionIndex);
      if (select.value) selected[index] = select.value;
    });

    const isSizeControl = (select) => {
      const name = String(select.getAttribute('aria-label') || '').toLowerCase();
      return name.includes('pointure') || name.includes('taille') || name.includes('size');
    };

    controls.forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex);
      const sizeControl = isSizeControl(select);

      Array.from(select.options).forEach((option) => {
        let available = false;

        if (sizeControl) {
          available = variants.some((variant) => {
            if (!variant.available) return false;
            if (String(variant.options?.[optionIndex]) !== String(option.value)) return false;

            return controls.every((otherControl) => {
              if (otherControl === select) return true;
              if (isSizeControl(otherControl)) return true;

              const otherIndex = Number(otherControl.dataset.optionIndex);
              return String(variant.options?.[otherIndex]) === String(otherControl.value);
            });
          });
        } else {
          available = variants.some((variant) => {
            if (!variant.available) return false;
            return String(variant.options?.[optionIndex]) === String(option.value);
          });
        }

        option.disabled = !available;
        option.dataset.unavailable = available ? 'false' : 'true';

        if (sizeControl) {
          const baseLabel = option.dataset.baseLabel || option.textContent.replace(/\s+—\s+Rupture$/i, '').replace(/[̶]/g, '');
          option.dataset.baseLabel = baseLabel;
          option.textContent = baseLabel;
        }
      });

      if (sizeControl) {
        syncCustomSizePicker(select);
      } else {
        syncCustomColorPicker(select);
      }
    });
  };

  const populateVariantControls = (root) => {
    if (!root || root.dataset.vimaPopulated === 'true') return;
    try {
      const variants = JSON.parse(root.dataset.variants || '[]');
      const currentOptions = JSON.parse(root.dataset.currentOptions || '[]');
      const controls = Array.from(root.querySelectorAll('[data-vima-variant-option]'));

      controls.forEach((select) => {
        const optionIndex = Number(select.dataset.optionIndex);
        const values = [];

        const currentColor = String(currentOptions[0] || '');
        const currentModel = /^(PAU|YOGI)\b/i.exec(currentColor)?.[1]?.toUpperCase() || '';

        variants.forEach((variant) => {
          const value = variant.options?.[optionIndex];
          if (value == null) return;
          if (optionIndex === 0 && currentModel) {
            const candidateModel = /^(PAU|YOGI)\b/i.exec(String(value))?.[1]?.toUpperCase() || '';
            if (candidateModel && candidateModel !== currentModel) return;
          }
          if (!values.includes(String(value))) values.push(String(value));
        });

        select.replaceChildren();
        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.selected = String(currentOptions[optionIndex] ?? '') === value;
          select.appendChild(option);
        });
      });

      refreshVariantAvailability(root);
      root.dataset.vimaPopulated = 'true';
    } catch {}
  };

  const swapVariantFromControls = async (root, changedSelect) => {
    if (!root) return;
    const variants = JSON.parse(root.dataset.variants || '[]');
    const currentOptions = JSON.parse(root.dataset.currentOptions || '[]');
    const controls = Array.from(root.querySelectorAll('[data-vima-variant-option]'));
    if (variantSwapInFlight) {
      controls.forEach((select) => (select.value = currentOptions[Number(select.dataset.optionIndex)]));
      refreshVariantAvailability(root);
      return;
    }
    const desired = currentOptions.slice();

    controls.forEach((select) => {
      desired[Number(select.dataset.optionIndex)] = select.value;
    });

    let nextVariant = variants.find((variant) =>
      variant.available && (variant.options || []).every((value, index) => String(value) === String(desired[index]))
    );

    if (!nextVariant && changedSelect) {
      const changedIndex = Number(changedSelect.dataset.optionIndex);
      nextVariant = variants.find((variant) => {
        if (!variant.available) return false;
        return String(variant.options?.[changedIndex]) === String(changedSelect.value);
      });
    }

    if (!nextVariant || String(nextVariant.id) === String(root.dataset.currentVariant)) return;

    const itemCard = root.closest('.vima-cart__item');
    const itemImage = itemCard?.querySelector('.vima-cart__image');
    const variantImage =
      nextVariant.featured_image?.src ||
      nextVariant.featured_media?.preview_image?.src ||
      nextVariant.image?.src ||
      nextVariant.image;

    if (itemImage && variantImage) {
      itemImage.removeAttribute('srcset');
      itemImage.src = variantImage;
    }

    variantSwapInFlight = true;
    const drawer = document.querySelector('cart-drawer');
    const inner = drawer?.querySelector('.drawer__inner');
    const wasInert = inner?.inert;
    if (inner) inner.inert = true;
    controls.forEach((select) => (select.disabled = true));
    const errorElement = document.getElementById('CartDrawer-CartErrors');
    if (errorElement) errorElement.textContent = '';
    let errorMessage = '';
    let before;
    let original;
    let targetMatches;
    let originalMatches;
    let targetQuantity = 0;
    let originalQuantity = 0;
    let removalAttempted = false;
    const readCart = async () => {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('cart');
      return response.json();
    };
    const propertiesKey = (properties) => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
    const configurationMatches = (item, variantId) => String(item.variant_id) === String(variantId)
      && propertiesKey(item.properties) === propertiesKey(original.properties)
      && String(item.selling_plan_allocation?.selling_plan?.id || '') === String(original.selling_plan_allocation?.selling_plan?.id || '');
    const totalQuantity = (cart, predicate) => (cart.items || []).filter(predicate).reduce((total, item) => total + Number(item.quantity), 0);
    try {
      before = await readCart();
      original = before.items.find((item) => item.key === root.dataset.lineKey);
      if (!original) throw new Error('line');
      targetMatches = (item) => configurationMatches(item, nextVariant.id);
      originalMatches = (item) => configurationMatches(item, original.variant_id);
      targetQuantity = totalQuantity(before, targetMatches);
      originalQuantity = totalQuantity(before, originalMatches);
      const sellingPlan = original.selling_plan_allocation?.selling_plan?.id;
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{
          id: nextVariant.id,
          quantity: original.quantity,
          properties: original.properties || {},
          ...(sellingPlan ? { selling_plan: sellingPlan } : {})
        }] })
      });
      const afterAdd = await readCart();
      if (!response.ok || totalQuantity(afterAdd, targetMatches) !== targetQuantity + original.quantity) {
        const error = new Error(response.status === 422 || response.ok ? 'stock' : 'add');
        throw error;
      }
      const currentOriginal = afterAdd.items.find((item) => item.key === original.key)
        || afterAdd.items.find((item) => originalMatches(item) && item.quantity === original.quantity);
      if (!currentOriginal) throw new Error('line');
      removalAttempted = true;
      const removed = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: currentOriginal.key,
          quantity: 0,
          sections,
          sections_url: window.location.pathname
        })
      }).then((r) => {
        if (!r.ok) throw new Error('remove');
        return r.json();
      });
      if (totalQuantity(removed, targetMatches) !== targetQuantity + original.quantity
        || totalQuantity(removed, originalMatches) !== originalQuantity - original.quantity) throw new Error('verify');
    } catch (error) {
      errorMessage = error.message === 'stock' ? root.dataset.swapStockError : root.dataset.swapError;
      if (original && targetMatches) {
        try {
          const current = await readCart();
          const completed = removalAttempted
            && totalQuantity(current, originalMatches) === originalQuantity - original.quantity
            && totalQuantity(current, targetMatches) === targetQuantity + original.quantity;
          if (completed) {
            errorMessage = '';
          } else if (totalQuantity(current, originalMatches) === originalQuantity) {
            let remaining = targetQuantity;
            const updates = {};
            current.items.filter(targetMatches).forEach((item) => {
              const keep = Math.min(remaining, item.quantity);
              remaining -= keep;
              if (keep !== item.quantity) updates[item.key] = keep;
            });
            if (Object.keys(updates).length) {
              const response = await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ updates })
              });
              if (!response.ok) throw new Error('restore');
            }
            const restored = await readCart();
            if (totalQuantity(restored, targetMatches) !== targetQuantity
              || totalQuantity(restored, originalMatches) !== originalQuantity) throw new Error('restore');
          } else {
            errorMessage = root.dataset.swapError;
          }
        } catch {
          errorMessage = root.dataset.swapError;
        }
      }
    } finally {
      try {
        await refreshDrawer(true);
      } catch {
        errorMessage = root.dataset.swapError;
        controls.forEach((select, index) => (select.value = currentOptions[Number(select.dataset.optionIndex)] || currentOptions[index]));
        refreshVariantAvailability(root);
      }
      const currentError = document.getElementById('CartDrawer-CartErrors');
      if (currentError) currentError.textContent = errorMessage;
      if (inner) inner.inert = wasInert;
      controls.forEach((select) => (select.disabled = false));
      variantSwapInFlight = false;
    }
  };

  const loadRecommendations = async (root) => {
    if (!root || root.dataset.loaded === 'true') return;
    const fallbackProductId = root.dataset.productId;
    if (!fallbackProductId) return;
    root.dataset.loaded = 'true';
    const grid = root.querySelector('[data-vima-reco-grid]');
    if (!grid) return;

    try {
      const existing = new Set((root.dataset.cartProductIds || '').split(',').filter(Boolean));
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const preferredFit = vimaPreferredFit(cart);
      const productId = vimaRecentSeed(existing, fallbackProductId);
      const recommendationParams = 'product_id=' + encodeURIComponent(productId) + '&limit=12&intent=related';
      const namesRequest = fetch('/recommendations/products?section_id=vima-cart-recommendation-names&' + recommendationParams)
        .then((response) => { if (!response.ok) throw new Error('names'); return response.text(); })
        .then((html) => {
          const document = new DOMParser().parseFromString(html, 'text/html');
          return new Map(Array.from(document.querySelectorAll('[data-vima-recommendation-name]'), (element) => [element.dataset.productId, element.dataset.name]));
        })
        .catch(() => new Map());
      const [response, names] = await Promise.all([
        fetch('/recommendations/products.json?' + recommendationParams),
        namesRequest
      ]);
      const data = await response.json();
      const products = (data.products || [])
        .filter((product) => !existing.has(String(product.id)))
        .filter(vimaIsSeasonalRecommendation)
        .slice(0, 4);

      grid.replaceChildren();

      if (!products.length) {
        grid.innerHTML = '<div class="vima-cart__reco-loading">Aucune suggestion saisonnière pour le moment.</div>';
        return;
      }

      products.forEach((product) => {
        const displayName = names.get(String(product.id)) || product.title;
        const variant = vimaVariantForFit(product.variants || [], preferredFit);
        if (!variant) return;

        const card = document.createElement('article');
        card.className = 'vima-cart__reco-card';

        const image = document.createElement('img');
        image.loading = 'lazy';
        image.alt = displayName || '';
        image.src = product.featured_image || product.images?.[0] || '';
        card.appendChild(image);

        const info = document.createElement('div');
        info.className = 'vima-cart__reco-info';

        const link = document.createElement('a');
        link.className = 'vima-cart__reco-title';
        link.href = product.url;
        link.textContent = displayName;
        if (product.vendor) {
          const vendor = document.createElement('span');
          vendor.className = 'vima-cart__reco-size';
          vendor.textContent = product.vendor;
          info.appendChild(vendor);
        }
        info.appendChild(link);

        if (preferredFit?.cm || preferredFit?.size) {
          const size = document.createElement('span');
          size.className = 'vima-cart__reco-size';
          const matchedOption = (variant.options || []).find((option) => vimaCmRange(option) || vimaSizeNumbers(option).length);
          size.textContent = matchedOption ? 'Pointure ' + matchedOption : '';
          if (size.textContent) info.appendChild(size);
        }

        const bottom = document.createElement('div');
        bottom.className = 'vima-cart__reco-bottom';

        const price = document.createElement('span');
        price.className = 'vima-cart__reco-price';
        price.textContent = money(variant.price);
        bottom.appendChild(price);

        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'vima-cart__reco-add';
        add.textContent = '+';
        add.setAttribute('aria-label', 'Ajouter ' + (product.vendor ? product.vendor + ' ' : '') + displayName);
        add.dataset.variantId = variant.id;
        bottom.appendChild(add);

        info.appendChild(bottom);
        card.appendChild(info);
        grid.appendChild(card);
      });
    } catch {
      grid.innerHTML = '<div class="vima-cart__reco-loading">Suggestions indisponibles pour le moment.</div>';
    }
  };

  const syncQuantityMinimums = () => {
    document.querySelectorAll('.vima-cart__quantity').forEach((quantity) => {
      const input = quantity.querySelector('.quantity__input');
      const minus = quantity.querySelector('button[name="minus"]');
      if (!input) return;

      input.min = '1';
      input.dataset.min = '1';

      const clamp = () => {
        if (Number(input.value || 1) < 1) input.value = '1';
        if (minus) minus.disabled = Number(input.value || 1) <= 1;
      };

      clamp();

      if (input.dataset.vimaMinReady !== 'true') {
        input.dataset.vimaMinReady = 'true';
        input.addEventListener('input', clamp);
        input.addEventListener('change', clamp);
      }
    });
  };

  const syncEnhancements = () => {
    document.querySelectorAll('[data-vima-variant-controls]').forEach(populateVariantControls);
    loadRecommendations(document.querySelector('[data-vima-recommendations]'));
    syncQuantityMinimums();
  };

  document.addEventListener('change', async (event) => {
    const quantityInput = event.target.closest('.vima-cart__quantity .quantity__input');
    if (quantityInput) {
      const previous = Number(quantityInput.getAttribute('value') || 1);
      const min = Number(quantityInput.min || 1);
      const max = quantityInput.max ? Number(quantityInput.max) : Infinity;
      const next = Math.max(min, Math.min(max, Number(quantityInput.value || min)));
      quantityInput.value = String(next);
      await vimaUpdateLineQuantity(quantityInput, next, previous);
      return;
    }

    const select = event.target.closest('[data-vima-variant-option]');
    if (select) {
      const root = select.closest('[data-vima-variant-controls]');
      refreshVariantAvailability(root);
      swapVariantFromControls(root, select);
    }
  });

  const vimaUpdateLineQuantity = async (input, next, previous) => {
    const lineKey = input?.dataset.quantityLineKey;
    if (!lineKey) return false;

    try {
      const changed = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: next,
          sections,
          sections_url: window.location.pathname
        })
      }).then((response) => {
        if (!response.ok) throw new Error('quantity');
        return response.json();
      });

      const drawer = document.querySelector('cart-drawer');
      if (drawer && changed.sections) {
        drawer.renderContents(changed);
      } else {
        window.location.reload();
      }
      return true;
    } catch (error) {
      if (input) input.value = String(previous);
      console.error('[VIMA cart] quantity update failed', error);
      return false;
    }
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.vima-cart__quantity .quantity__button');
    if (!button) return;

    const quantity = button.closest('.vima-cart__quantity');
    const input = quantity?.querySelector('.quantity__input');
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const lineKey = input.dataset.quantityLineKey;
    if (!lineKey) return;

    const current = Number(input.value || 1);
    const step = Number(input.step || 1);
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    const next = button.name === 'plus'
      ? Math.min(max, current + step)
      : Math.max(min, current - step);

    if (next === current) return;

    button.disabled = true;
    input.value = String(next);
    await vimaUpdateLineQuantity(input, next, current);
    button.disabled = false;
  }, true);

  document.addEventListener('click', async (event) => {
    const sizeTrigger = event.target.closest('.vima-cart__size-trigger');
    if (sizeTrigger) {
      const picker = sizeTrigger.closest('.vima-cart__size-picker');
      const menu = picker?.querySelector('.vima-cart__size-menu');
      const item = sizeTrigger.closest('.vima-cart__item');
      if (!menu) return;

      const opening = menu.hidden;

      document.querySelectorAll('.vima-cart__size-menu').forEach((other) => { other.hidden = true; });
      document.querySelectorAll('.vima-cart__size-trigger').forEach((other) => other.setAttribute('aria-expanded', 'false'));
      document.querySelectorAll('.vima-cart__size-picker.is-open').forEach((other) => other.classList.remove('is-open'));
      document.querySelectorAll('.vima-cart__item.has-open-size').forEach((other) => other.classList.remove('has-open-size'));

      menu.hidden = !opening;
      picker?.classList.toggle('is-open', opening);
      item?.classList.toggle('has-open-size', opening);
      sizeTrigger.setAttribute('aria-expanded', opening ? 'true' : 'false');
      return;
    }

    const sizeOption = event.target.closest('.vima-cart__size-option');
    if (sizeOption && !sizeOption.disabled) {
      const picker = sizeOption.closest('.vima-cart__size-picker');
      const label = picker?.closest('.vima-cart__variant-control');
      const select = label?.querySelector('[data-vima-variant-option]');
      if (!select) return;
      select.value = sizeOption.dataset.value;
      picker?.classList.remove('is-open');
      picker?.closest('.vima-cart__item')?.classList.remove('has-open-size');
      picker?.querySelector('.vima-cart__size-menu')?.setAttribute('hidden', '');
      picker?.querySelector('.vima-cart__size-trigger')?.setAttribute('aria-expanded', 'false');
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const colorTrigger = event.target.closest('.vima-cart__color-trigger');
    if (colorTrigger) {
      const picker = colorTrigger.closest('.vima-cart__color-picker');
      const menu = picker?.querySelector('.vima-cart__color-menu');
      const item = colorTrigger.closest('.vima-cart__item');
      if (!menu) return;

      const opening = menu.hidden;

      document.querySelectorAll('.vima-cart__size-menu, .vima-cart__color-menu').forEach((other) => { other.hidden = true; });
      document.querySelectorAll('.vima-cart__size-trigger, .vima-cart__color-trigger').forEach((other) => other.setAttribute('aria-expanded', 'false'));
      document.querySelectorAll('.vima-cart__size-picker.is-open, .vima-cart__color-picker.is-open').forEach((other) => other.classList.remove('is-open'));
      document.querySelectorAll('.vima-cart__item.has-open-size, .vima-cart__item.has-open-color').forEach((other) => {
        other.classList.remove('has-open-size');
        other.classList.remove('has-open-color');
      });

      menu.hidden = !opening;
      picker?.classList.toggle('is-open', opening);
      item?.classList.toggle('has-open-color', opening);
      colorTrigger.setAttribute('aria-expanded', opening ? 'true' : 'false');
      return;
    }

    const colorOption = event.target.closest('.vima-cart__color-option');
    if (colorOption && !colorOption.disabled) {
      const picker = colorOption.closest('.vima-cart__color-picker');
      const label = picker?.closest('.vima-cart__variant-control');
      const select = label?.querySelector('[data-vima-variant-option]');
      if (!select) return;
      select.value = colorOption.dataset.value;
      picker?.classList.remove('is-open');
      picker?.closest('.vima-cart__item')?.classList.remove('has-open-color');
      picker?.querySelector('.vima-cart__color-menu')?.setAttribute('hidden', '');
      picker?.querySelector('.vima-cart__color-trigger')?.setAttribute('aria-expanded', 'false');
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const giftButton = event.target.closest('[data-vima-gift-add]');
    if (giftButton) {
      const select = document.getElementById('VimaGiftVariant');
      const variantId = Number(select?.value || giftButton.dataset.variantId || document.querySelector('cart-drawer')?.dataset.vimaGiftVariant);
      if (!variantId) return;
      giftButton.disabled = true;
      try {
        const added = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties: { _vima_gift: 'true' } }],
            sections,
            sections_url: window.location.pathname
          })
        }).then((r) => r.json());
        const drawer = document.querySelector('cart-drawer');
        if (drawer && added.sections) drawer.renderContents(added);
      } finally {
        giftButton.disabled = false;
      }
      return;
    }

    const recoButton = event.target.closest('.vima-cart__reco-add');
    if (recoButton) {
      const variantId = Number(recoButton.dataset.variantId);
      if (!variantId) return;
      recoButton.disabled = true;
      try {
        const added = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname })
        }).then((r) => (r.ok ? r.json() : null));
        if (!added) {
          window.alert('Ajout impossible pour le moment. Veuillez réessayer.');
          return;
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer && added.sections) drawer.renderContents(added);
      } catch (error) {
        window.alert('Ajout impossible pour le moment. Veuillez réessayer.');
      } finally {
        recoButton.disabled = false;
      }
    }
  });

  const renderAppliedDiscountCode = (code) => {
    const container = document.querySelector('[data-vima-applied-codes]');
    if (!container) return;
    container.innerHTML = '';
    if (!code) return;

    const chip = document.createElement('span');
    chip.className = 'vima-cart__applied-code';

    const label = document.createElement('span');
    label.textContent = code;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'vima-cart__discount-remove';
    button.setAttribute('data-vima-discount-remove', '');
    button.setAttribute('aria-label', 'Retirer le code promo ' + code);
    button.setAttribute('title', 'Retirer le code promo');
    button.textContent = '×';

    chip.append(label, button);
    container.appendChild(chip);
  };

  const getStoredDiscountCode = () => {
    try {
      return sessionStorage.getItem('vimaAppliedDiscountCode') || '';
    } catch {
      return '';
    }
  };

  const setStoredDiscountCode = (code) => {
    try {
      if (code) sessionStorage.setItem('vimaAppliedDiscountCode', code);
      else sessionStorage.removeItem('vimaAppliedDiscountCode');
    } catch {}
  };

  renderAppliedDiscountCode(getStoredDiscountCode());

  document.addEventListener('click', async (event) => {
    const removeDiscount = event.target.closest('[data-vima-discount-remove]');
    if (!removeDiscount) return;

    removeDiscount.disabled = true;
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ discount: '' })
      });
      if (!response.ok) throw new Error('discount-remove');
      setStoredDiscountCode('');
      await refreshDrawer(true);
      renderAppliedDiscountCode('');
    } catch {
      removeDiscount.disabled = false;
      const status = document.querySelector('[data-vima-discount-status]');
      if (status) status.textContent = 'Impossible de retirer ce code pour le moment.';
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-vima-discount-form]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('input[name="discount"]');
    const status = form.querySelector('[data-vima-discount-status]');
    const code = input?.value.trim();
    if (!code) return;

    const discountDetails = form.closest('.vima-cart__discount-accordion');
    const wasDiscountOpen = Boolean(discountDetails?.open);

    status.textContent = 'Application du code…';
    try {
      const response = await fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart', {
        credentials: 'same-origin',
        redirect: 'follow',
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('discount');

      const cartResponse = await fetch('/cart.js?discount=' + encodeURIComponent(code), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) throw new Error('cart');

      const cart = await cartResponse.json();
      const normalizedCode = code.toLowerCase();
      const applications = [];

      for (const application of cart.cart_level_discount_applications || []) applications.push(application);
      for (const item of cart.items || []) {
        for (const allocation of item.line_level_discount_allocations || []) {
          if (allocation?.discount_application) applications.push(allocation.discount_application);
        }
      }

      const matchedCartDiscount = (cart.cart_level_discount_applications || []).some((application) =>
        String(application?.title || '').trim().toLowerCase() === normalizedCode &&
        Number(application.total_allocated_amount || 0) > 0
      );

      const matchedLineDiscount = (cart.items || []).some((item) =>
        (item.line_level_discount_allocations || []).some((allocation) =>
          String(allocation?.discount_application?.title || '').trim().toLowerCase() === normalizedCode &&
          Number(allocation.amount || 0) > 0
        )
      );

      const codeApplied = matchedCartDiscount || matchedLineDiscount;
      setStoredDiscountCode(codeApplied ? code : '');
      await refreshDrawer(true);
      renderAppliedDiscountCode(codeApplied ? code : '');

      const refreshedDetails = document.querySelector('.vima-cart__discount-accordion');
      if (wasDiscountOpen && refreshedDetails) refreshedDetails.open = true;

      const currentStatus = document.querySelector('[data-vima-discount-status]');
      if (!currentStatus) return;
      currentStatus.textContent = codeApplied
        ? 'Code promo appliqué.'
        : 'Ce code n’est pas applicable à votre panier.';
    } catch {
      const currentStatus = document.querySelector('[data-vima-discount-status]') || status;
      if (currentStatus) currentStatus.textContent = 'Impossible de vérifier ce code pour le moment.';
    }
  });

  const closeVimaSizePickers = (event) => {
    if (event?.target?.closest?.('.vima-cart__size-picker, .vima-cart__color-picker')) return;
    document.querySelectorAll('.vima-cart__size-menu, .vima-cart__color-menu').forEach((menu) => { menu.hidden = true; });
    document.querySelectorAll('.vima-cart__size-trigger, .vima-cart__color-trigger').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    document.querySelectorAll('.vima-cart__size-picker.is-open, .vima-cart__color-picker.is-open').forEach((picker) => picker.classList.remove('is-open'));
    document.querySelectorAll('.vima-cart__item.has-open-size, .vima-cart__item.has-open-color').forEach((item) => {
      item.classList.remove('has-open-size');
      item.classList.remove('has-open-color');
    });
  };

  document.addEventListener('click', closeVimaSizePickers);

  const observer = new MutationObserver(syncEnhancements);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', syncEnhancements, { once: true });
  syncEnhancements();
})();


